// src/pages/AdminConfiguracionFiscal.js
// =========================================================================
// 🏢 DATOS FISCALES DE LA CLÍNICA (identidad del emisor de las facturas)
//
// Sin estos datos el backend no deja emitir ninguna factura (409): toda
// factura debe identificar a quien la emite (NIF, razón social, domicilio).
// Solo el rol 'admin' puede editarlos (PUT /api/empresa). Cada factura
// guarda una COPIA de estos datos al emitirse, así que un cambio aquí solo
// afecta a las facturas que se emitan a partir de ese momento.
// =========================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const VACIO = {
    razonSocial: '',
    nif: '',
    via: '',
    codigoPostal: '',
    municipio: '',
    provincia: '',
    pais: 'ES',
    telefono: '',
    email: '',
    modalidad: 'VERIFACTU'
};

const inputBase = 'w-full p-3 bg-slate-50 border rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 transition-colors';
const inputOk = 'border-slate-200 focus:border-clinica-primary focus:ring-clinica-primary/15';
const inputError = 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/15';

// Validación rápida en el navegador (el backend vuelve a validar, incluido el
// dígito de control del NIF/CIF, y su mensaje es el que manda).
function validar(f) {
    const e = {};
    if (f.razonSocial.trim().length < 2) e.razonSocial = 'Indica la razón social (nombre legal de la clínica).';
    if (!/^[A-Z0-9]{9}$/i.test(f.nif.replace(/[\s-]/g, ''))) e.nif = 'El NIF/CIF debe tener 9 caracteres (p. ej. B12345674).';
    if (f.via.trim().length < 3) e.via = 'Indica la calle y el número.';
    if (!/^\d{5}$/.test(f.codigoPostal.trim())) e.codigoPostal = 'El código postal tiene 5 dígitos.';
    if (f.municipio.trim().length < 2) e.municipio = 'Indica el municipio.';
    if (f.provincia.trim().length < 2) e.provincia = 'Indica la provincia.';
    if (!/^[A-Z]{2}$/i.test(f.pais.trim())) e.pais = 'Código de país de 2 letras (ES).';
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Email no válido.';
    return e;
}

// El backend devuelve 'Errores de validación: nif : NIF/CIF inválido… | domicilio.via : …'.
// Cada error con ruta se asigna a su campo del formulario (domicilio.via → via);
// lo que no corresponda a ningún campo se devuelve como error general.
function repartirErrorServidor(mensaje) {
    const texto = String(mensaje).replace(/^Errores de validación:\s*/i, '');
    const porCampo = {};
    const generales = [];
    texto.split(/\s*\|\s*/).forEach((parte) => {
        const m = parte.match(/^([\w.]+)\s:\s(.+)$/);
        const campo = m && m[1].split('.').pop();
        if (campo && campo in VACIO) porCampo[campo] = m[2];
        else generales.push(m ? m[2] : parte);
    });
    return { porCampo, general: generales.join(' · ') };
}

function Campo({ id, label, error, ayuda, children }) {
    return (
        <div>
            <label htmlFor={id} className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">{label}</label>
            {children}
            {error
                ? <p className="mt-1.5 text-xs font-semibold text-rose-600">{error}</p>
                : ayuda && <p className="mt-1.5 text-xs font-medium text-slate-400">{ayuda}</p>}
        </div>
    );
}

export default function AdminConfiguracionFiscal() {
    const { showToast } = useToast();
    // showToast cambia de identidad en cada render del proveedor: lo leemos de
    // una ref para que 'cargar' sea estable y no se relance en bucle.
    const toastRef = useRef(showToast);
    toastRef.current = showToast;
    const [form, setForm] = useState(VACIO);
    const [configurada, setConfigurada] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [errores, setErrores] = useState({});
    const [errorServidor, setErrorServidor] = useState('');

    const cargar = useCallback(async () => {
        try {
            setCargando(true);
            const res = await api.get('/empresa');
            if (res?.configurada) {
                setConfigurada(true);
                setForm({
                    razonSocial: res.razonSocial || '',
                    nif: res.nif || '',
                    via: res.domicilio?.via || '',
                    codigoPostal: res.domicilio?.codigoPostal || '',
                    municipio: res.domicilio?.municipio || '',
                    provincia: res.domicilio?.provincia || '',
                    pais: res.domicilio?.pais || 'ES',
                    telefono: res.telefono || '',
                    email: res.email || '',
                    modalidad: res.modalidad || 'VERIFACTU'
                });
            } else {
                setConfigurada(false);
            }
        } catch (err) {
            toastRef.current(err.message || 'No se pudieron cargar los datos fiscales.', 'error');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const set = (campo, valor) => {
        setForm(f => ({ ...f, [campo]: valor }));
        if (errores[campo]) setErrores(e => ({ ...e, [campo]: undefined }));
        if (errorServidor) setErrorServidor('');
    };

    const guardar = async (e) => {
        e.preventDefault();
        const encontrados = validar(form);
        setErrores(encontrados);
        if (Object.keys(encontrados).length > 0) return;

        // El backend no acepta strings vacíos en los campos opcionales
        const payload = {
            razonSocial: form.razonSocial.trim(),
            nif: form.nif.replace(/[\s-]/g, '').toUpperCase(),
            domicilio: {
                via: form.via.trim(),
                codigoPostal: form.codigoPostal.trim(),
                municipio: form.municipio.trim(),
                provincia: form.provincia.trim(),
                pais: form.pais.trim().toUpperCase()
            },
            modalidad: form.modalidad,
            ...(form.telefono.trim() && { telefono: form.telefono.trim() }),
            ...(form.email.trim() && { email: form.email.trim() })
        };

        try {
            setGuardando(true);
            await api.put('/empresa', payload);
            showToast('Datos fiscales guardados. Se aplicarán a las facturas que se emitan desde ahora.', 'success');
            setConfigurada(true);
            cargar();
        } catch (err) {
            const { porCampo, general } = repartirErrorServidor(err.message || 'No se pudieron guardar los datos fiscales.');
            setErrores(porCampo);
            setErrorServidor(general);
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) {
        return (
            <div className="p-6 md:p-8 max-w-4xl mx-auto">
                <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse mb-6"></div>
                <div className="h-96 bg-white border border-slate-200 rounded-[2rem] animate-pulse"></div>
            </div>
        );
    }

    const cls = (campo) => `${inputBase} ${errores[campo] ? inputError : inputOk}`;

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto animate-fade-in">
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Datos fiscales de la clínica</h1>
                <p className="text-sm font-semibold text-slate-500 mt-1">Identidad del emisor que aparece en todas las facturas.</p>
            </div>

            {!configurada ? (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                    <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                    <p className="text-sm font-semibold text-amber-800">
                        Todavía no hay datos fiscales. Mientras falten, <span className="font-black">no se puede emitir ninguna factura</span>.
                    </p>
                </div>
            ) : (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-sky-50 border border-sky-200">
                    <svg className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <p className="text-sm font-semibold text-sky-800">
                        Cada factura guarda una copia de estos datos al emitirse. Si los cambias, las facturas ya emitidas no se modifican.
                    </p>
                </div>
            )}

            <form onSubmit={guardar} noValidate className="bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-8">
                <section className="space-y-5">
                    <h2 className="text-lg font-black text-slate-800 border-b border-slate-100 pb-3">Identificación</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="md:col-span-2">
                            <Campo id="fiscal-razon" label="Razón social" error={errores.razonSocial} ayuda="Nombre legal, tal como figura en Hacienda (p. ej. Clínica Ejemplo SL).">
                                <input id="fiscal-razon" type="text" value={form.razonSocial} onChange={(e) => set('razonSocial', e.target.value)} className={cls('razonSocial')} maxLength={120} />
                            </Campo>
                        </div>
                        <Campo id="fiscal-nif" label="NIF / CIF" error={errores.nif}>
                            <input id="fiscal-nif" type="text" value={form.nif} onChange={(e) => set('nif', e.target.value.toUpperCase())} className={`${cls('nif')} font-mono tracking-wider`} maxLength={12} />
                        </Campo>
                    </div>
                </section>

                <section className="space-y-5">
                    <h2 className="text-lg font-black text-slate-800 border-b border-slate-100 pb-3">Domicilio fiscal</h2>
                    <Campo id="fiscal-via" label="Calle y número" error={errores.via}>
                        <input id="fiscal-via" type="text" value={form.via} onChange={(e) => set('via', e.target.value)} className={cls('via')} />
                    </Campo>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        <Campo id="fiscal-cp" label="Código postal" error={errores.codigoPostal}>
                            <input id="fiscal-cp" type="text" inputMode="numeric" value={form.codigoPostal} onChange={(e) => set('codigoPostal', e.target.value)} className={cls('codigoPostal')} maxLength={5} />
                        </Campo>
                        <Campo id="fiscal-municipio" label="Municipio" error={errores.municipio}>
                            <input id="fiscal-municipio" type="text" value={form.municipio} onChange={(e) => set('municipio', e.target.value)} className={cls('municipio')} />
                        </Campo>
                        <Campo id="fiscal-provincia" label="Provincia" error={errores.provincia}>
                            <input id="fiscal-provincia" type="text" value={form.provincia} onChange={(e) => set('provincia', e.target.value)} className={cls('provincia')} />
                        </Campo>
                        <Campo id="fiscal-pais" label="País" error={errores.pais}>
                            <input id="fiscal-pais" type="text" value={form.pais} onChange={(e) => set('pais', e.target.value.toUpperCase())} className={cls('pais')} maxLength={2} />
                        </Campo>
                    </div>
                </section>

                <section className="space-y-5">
                    <h2 className="text-lg font-black text-slate-800 border-b border-slate-100 pb-3">Contacto (opcional)</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Campo id="fiscal-telefono" label="Teléfono" error={errores.telefono}>
                            <input id="fiscal-telefono" type="tel" value={form.telefono} onChange={(e) => set('telefono', e.target.value)} className={cls('telefono')} />
                        </Campo>
                        <Campo id="fiscal-email" label="Email" error={errores.email}>
                            <input id="fiscal-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={cls('email')} />
                        </Campo>
                    </div>
                </section>

                <section className="space-y-5">
                    <h2 className="text-lg font-black text-slate-800 border-b border-slate-100 pb-3">Sistema de facturación</h2>
                    <Campo id="fiscal-modalidad" label="Modalidad" ayuda="VERI*FACTU: los registros se envían a la AEAT según se emiten. Consulta con tu gestoría cuál te corresponde.">
                        <select id="fiscal-modalidad" value={form.modalidad} onChange={(e) => set('modalidad', e.target.value)} className={cls('modalidad')}>
                            <option value="VERIFACTU">VERI*FACTU</option>
                            <option value="NO_VERIFACTU">No VERI*FACTU</option>
                        </select>
                    </Campo>
                </section>

                {errorServidor && (
                    <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700" role="alert">
                        {errorServidor}
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={guardando}
                        className="px-6 py-3 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors shadow-md disabled:opacity-60"
                    >
                        {guardando ? 'Guardando…' : (configurada ? 'Guardar cambios' : 'Guardar datos fiscales')}
                    </button>
                </div>
            </form>
        </div>
    );
}
